import { createRouter, createWebHashHistory } from 'vue-router';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import routes from './routes';
import trackingEvents from '@/config/trackingEvents';
import { authHelpers } from '@/services/auth/auth-helper';
import { logAppError } from '@/config/sentryConfig';
import useAuthStore from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import { resetAllStores } from '@/utils/resetStores';

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Return to saved position when using browser back/forward
    return savedPosition || { top: 0 };
  }
});

// Helper functions
const isUnguardedRoute = (route) => route.meta?.unguarded === true;

const isUserAuthenticated = () => {
  const authStore = useAuthStore();
  return authStore.isUserAuthenticated;
};

const checkTenantAvailability = (tenantId) => {
  const permissionsStore = usePermissionsStore();
  return permissionsStore.hasTenantAccess(tenantId);
};

const isTenantRoute = (route) => {
  return (
    route.path.startsWith('/tenant') &&
    route.params.hasOwnProperty('tenantId') &&
    route.params.tenantId
  );
};

// Helper to save and retrieve intended route
const INTENDED_ROUTE_KEY = 'intendedRoute';

const saveIntendedRoute = (route) => {
  // Don't save login or error routes
  if (route.name === 'login' || route.name === 'error') return;

  // Save the full route object for complete restoration
  sessionStorage.setItem(
    INTENDED_ROUTE_KEY,
    JSON.stringify({
      path: route.fullPath,
      name: route.name,
      params: route.params,
      query: route.query
    })
  );
};

const getAndClearIntendedRoute = () => {
  const savedRoute = sessionStorage.getItem(INTENDED_ROUTE_KEY);
  if (savedRoute) {
    sessionStorage.removeItem(INTENDED_ROUTE_KEY);
    try {
      return JSON.parse(savedRoute);
    } catch (e) {
      console.error('Failed to parse intended route:', e);
      return null;
    }
  }
  return null;
};

// Authentication guard
const authGuard = async (to, from, next) => {
  // Skip authentication checks for unguarded routes
  if (isUnguardedRoute(to)) {
    return next();
  }

  // ...and continue if user is authenticated
  if (isUserAuthenticated()) {
    const authStore = useAuthStore();

    // Inconsistent state: auth flag is true but profile never loaded.
    // Force a clean sign-out so the user gets a fresh session.
    if (!authStore.userProfile) {
      logAppError({
        error: new Error(
          'Inconsistent auth state: authenticated but no user profile loaded'
        ),
        functionName: 'authGuard',
        section: 'router',
        level: 'warning'
      });

      try {
        await signOut();
      } catch (error) {
        logAppError({
          error,
          functionName: 'authGuard',
          section: 'signOut_cleanup',
          level: 'warning',
          message: 'Failed to sign out during inconsistent auth state recovery'
        });
      }
      resetAllStores();
      sessionStorage.clear();
      saveIntendedRoute(to);
      return next({ name: 'login' });
    }

    if (to.name === 'landing') {
      const intendedRoute = getAndClearIntendedRoute();
      if (intendedRoute) {
        return await next(intendedRoute);
      }
    }
    return next();
  }

  // Try to get current authenticated user from Cognito
  try {
    const currentUser = await getCurrentUser();

    // Check if user needs MFA verification or setup
    if (
      authHelpers.needsMFAVerification(currentUser) ||
      authHelpers.needsMFASetup(currentUser)
    ) {
      // If trying to access a protected route but MFA is required, redirect to login
      if (to.name !== 'login') {
        saveIntendedRoute(to);
        return next({ name: 'login' });
      }

      // Allow access to login page to handle MFA
      return next();
    }

    // User is authenticated but not reflected in store yet
    const authStore = useAuthStore();
    authStore.setUserAuthenticated(true);

    // Check for intended route after authentication
    if (to.name === 'landing') {
      const intendedRoute = getAndClearIntendedRoute();
      if (intendedRoute) {
        return next(intendedRoute);
      }
    }

    return next();
  } catch {
    // User is not authenticated, redirect to login unless already going there
    if (to.name === 'login') {
      return next();
    }

    // Save the intended destination before redirecting to login
    saveIntendedRoute(to);
    return next({ name: 'login' });
  }
};

// Tenant Availability Guard
const tenantAvailabilityGuard = async (to, from, next) => {
  // Skip tenant checks if user isn't authenticated yet
  if (!isUserAuthenticated()) {
    return next();
  }

  if (isTenantRoute(to)) {
    const tenantId = to.params.tenantId;
    if (checkTenantAvailability(tenantId)) {
      return next();
    } else {
      // Tenant not available for this user
      console.error(`Tenant ${tenantId} not available for current user`);
      return next({ name: 'notFound' });
    }
  }

  next();
};

// Tenant Selection Guard
const tenantSelectionGuard = async (to, from, next) => {
  // Skip tenant selection if user isn't authenticated yet
  if (!isUserAuthenticated()) {
    return next();
  }

  if (isTenantRoute(to)) {
    const tenantId = to.params.tenantId;

    // Set selected tenant in permissions store
    const permissionsStore = usePermissionsStore();
    permissionsStore.selectedTenant = tenantId;
  }

  next();
};

const trackingHook = (to, from) => {
  // Skip tracking only when an unguarded route is visited without a session.
  // The analytics endpoint requires authentication, and firing here causes a
  // 401 → token refresh → redirect-to-login loop for unauthenticated users.
  if (isUnguardedRoute(to) && !isUserAuthenticated()) return;

  const fromName = from.name || 'NULL';
  const toName = to.name || 'NULL';

  // Skip tracking for navigation to same route
  if (toName === fromName && to.path === from.path) {
    return;
  }

  trackingEvents.viewPage(toName, fromName, to.params);
};

// Register guards
router.beforeEach(authGuard);
router.beforeEach(tenantAvailabilityGuard);
router.beforeResolve(tenantSelectionGuard);
router.afterEach(trackingHook);

// When a lazy-loaded route chunk fails (e.g. stale deployment — old chunk
// hashes no longer exist after a new release), reload once to pick up the
// new bundle rather than showing an unhandled error to the user.
router.onError((error, to) => {
  const isChunkLoadError =
    error.message?.includes("Couldn't resolve component") ||
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Unable to preload CSS for') ||
    error.name === 'ChunkLoadError';

  if (isChunkLoadError) {
    const reloadKey = `chunk-reload:${to.fullPath}`;
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1');
      window.location.assign(to.fullPath);
    } else {
      // Already reloaded once — avoid infinite loop, navigate to error page
      router.push({ name: 'notFound' });
    }
  }
});

export default router;
